import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * API клиент с автоматическим обновлением токенов
 * @contract Singleton Axios instance для всех API вызовов
 * @ensures Автоматический refresh токенов при истечении
 */

const API_BASE_URL = '/api/v1';

// Создаем Axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Хранилище для tokens (синхронизируется с Zustand store)
let accessToken: string | null = null;
let refreshToken: string | null = null;
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Установить токены для API клиента
 */
export function setTokens(access: string | null, refresh: string | null): void {
  accessToken = access;
  refreshToken = refresh;

  if (access) {
    localStorage.setItem('accessToken', access);
  } else {
    localStorage.removeItem('accessToken');
  }

  if (refresh) {
    localStorage.setItem('refreshToken', refresh);
  } else {
    localStorage.removeItem('refreshToken');
  }
}

/**
 * Загрузить токены из localStorage при инициализации
 */
export function loadTokens(): void {
  accessToken = localStorage.getItem('accessToken');
  refreshToken = localStorage.getItem('refreshToken');
}

/**
 * Получить текущий access token
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Очистить токены при logout
 */
export function clearTokens(): void {
  setTokens(null, null);
}

// Обработка очереди запросов после refresh
function processQueue(error: Error | null, token: string | null = null): void {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
}

// Request interceptor - добавляем токен к запросам
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * UP-24 (2026-05-26): защита от premature /login redirect.
 * `loginRedirectInFlight` гарантирует что одновременные 401-ы (типичны для
 * /integrations, где 5+ карточек делают параллельные status-запросы) не
 * приводят к multiple window.location.href присваиваниям + не зацикливают
 * navigation, пока браузер не успел уйти на /login.
 */
let loginRedirectInFlight = false;

// Response interceptor - обработка 401 и auto refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // UP-24 (2026-05-26): defensive resync с localStorage перед any 401-decision.
    // Bug history: `getDemoUser`/`getLocalUser` (auth.api.ts) обходят setTokens()
    // и пишут напрямую в localStorage (комментарий "setTokens сломан при сборке"),
    // оставляя module-level `accessToken`/`refreshToken` равными null. На странице
    // /integrations (5+ параллельных request'ов) первая 401 уходит в ветку
    // !refreshToken → clearTokens → forced /login redirect, даже если refresh
    // token валиден в localStorage. Resync позволяет recovery вместо instant
    // logout. Не влияет на normal login flow (setTokens уже синхронизирует оба
    // источника — resync read just reaffirms current state).
    if (status === 401 && !refreshToken && typeof localStorage !== 'undefined') {
      const lsRefresh = localStorage.getItem('refreshToken');
      const lsAccess = localStorage.getItem('accessToken');
      if (lsRefresh) {
        refreshToken = lsRefresh;
        if (lsAccess && !accessToken) accessToken = lsAccess;
      }
    }

    // 401 без refresh token (или после неудачного refresh) — сессия мертва.
    // Раньше interceptor пропускал такой ответ дальше, и пользователь
    // оставался на странице с 401-ошибками (без редиректа на /login).
    // Теперь чистим токены и форсим редирект, чтобы UX был "разлогинило".
    // Исключение: сам endpoint /auth/* (login, refresh, code/send) —
    // там 401 это бизнес-ответ, не "истёкшая сессия".
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (status === 401 && !refreshToken && !isAuthEndpoint) {
      if (!loginRedirectInFlight) {
        loginRedirectInFlight = true;
        clearTokens();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }

    // Если 401 и есть refresh token, пробуем обновить
    if (status === 401 && refreshToken && !originalRequest._retry) {
      if (isRefreshing) {
        // Если уже идет refresh, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

        setTokens(newAccessToken, newRefreshToken);
        processQueue(null, newAccessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        clearTokens();
        // UP-24 (2026-05-26): guard против multiple redirects при parallel refresh failures.
        if (!loginRedirectInFlight) {
          loginRedirectInFlight = true;
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Инициализируем токены при загрузке
loadTokens();
